import { useRef, useState } from 'react'

import { config } from '@skytech/config'
import { LoaderCircle, Wizard, WizardStep } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { getMaxDate, getIsDateInRange } from '@skytech/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { FormikProps } from 'formik'
import { Form, Formik } from 'formik'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { WorkflowAccelerateModal } from '@/components/dev/WorkflowAccelerateModal'
import { LoadingError } from '@/components/LoadingError'
import { useCanManageOnOffboarding } from '@/hooks/access'
import useUser, { useProfile } from '@/hooks/user'
import { SUB_SERVICE_ORDER_STATUS } from '@/modules/subscriptions/constants'
import terminationResources from '@/modules/subscriptions/termination/resources'
import { APP_ROUTES } from '@/util/app-routes'

import queries from '../queries'
import type { TerminationPayload, TerminationType } from '../types'

import { NewTerminationConfirm } from './NewTerminationConfirm'
import { NewTerminationDetails } from './NewTerminationDetails'
import { NewTerminationType } from './NewTerminationType'

const isDev = config.ENVIRONMENT === 'dev'

type FormFields = {
  terminationType?: TerminationType
  comment: string
  terminationDate: string
  costObjectId: number | null
}

export const NewTerminationForm = () => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const user = useUser()
  const profile = useProfile()
  const canManageOnOffboarding = useCanManageOnOffboarding()
  const queryClient = useQueryClient()
  const { setIsWizardMode } = useAppContext()
  const formRef = useRef<FormikProps<FormFields> | null>(null)
  const [pendingPayload, setPendingPayload] = useState<TerminationPayload | null>(null)

  const newTerminationMutation = useMutation(terminationResources.post, {
    onSuccess() {
      queryClient.invalidateQueries(queries.terminationList(), { refetchType: 'all' })
      navigate(
        `${APP_ROUTES.SUBSCRIPTIONS_TERMINATION_LIST}?status=${SUB_SERVICE_ORDER_STATUS.NEW}`
      )
    },
  })

  function getIsTerminationDateValid(terminationDate: string) {
    if (!terminationDate) return false
    return getIsDateInRange({
      minDate: new Date(),
      maxDate: getMaxDate(12),
      options: {
        date: new Date(terminationDate),
      },
    })
  }

  const onComplete = (values: FormFields) => {
    if (!formRef.current?.dirty) return
    const { terminationType, terminationDate, costObjectId, comment } = values
    if (!terminationType || !costObjectId) return

    const payload: TerminationPayload = { terminationType, costObjectId, terminationDate, comment }

    if (isDev) {
      setPendingPayload(payload)
    } else {
      newTerminationMutation.mutate(payload)
    }
  }

  const handleAccelerateConfirm = (delaySeconds: number) => {
    if (!pendingPayload) return
    newTerminationMutation.mutate({ ...pendingPayload, acceleratedDelaySeconds: delaySeconds })
    setPendingPayload(null)
  }

  const handleAccelerateSkip = () => {
    if (!pendingPayload) return
    newTerminationMutation.mutate(pendingPayload)
    setPendingPayload(null)
  }

  return (
    <Container onSwipedLeft={() => navigate(APP_ROUTES.HOME)}>
      {user && profile && !canManageOnOffboarding ? (
        <LoadingError />
      ) : user && !profile ? (
        <LoaderCircle />
      ) : (
        <>
          {isDev && (
            <WorkflowAccelerateModal
              isOpen={pendingPayload !== null}
              workflowLabel="Offboarding"
              onConfirm={handleAccelerateConfirm}
              onSkip={handleAccelerateSkip}
            />
          )}

          <Container.Header>
            <Translation id="New Termination" />
          </Container.Header>

          <Formik<FormFields>
            initialValues={{
              costObjectId: null,
              terminationType: undefined,
              terminationDate: '',
              comment: '',
            }}
            validate={values => {
              const errors: Partial<Record<keyof FormFields, string>> = {}
              if (!values.costObjectId) {
                errors.costObjectId = translate('This field is mandatory')
              }
              if (!values.terminationDate) {
                errors.terminationDate = translate('This field is mandatory')
              }
              if (values.terminationDate && !getIsTerminationDateValid(values.terminationDate)) {
                errors.terminationDate = translate(
                  'The provided date must fall between today and 1 year after today'
                )
              }
              if (!values.terminationType) {
                errors.terminationType = translate('This field is mandatory')
              }

              return errors
            }}
            innerRef={formRef}
            onSubmit={formValues => onComplete(formValues)}
          >
            {({ values }) => {
              const isTypeValid = values.terminationType !== undefined
              const isTerminationDetailsValid = Boolean(
                values.costObjectId && getIsTerminationDateValid(values.terminationDate)
              )

              return (
                <Form>
                  <Wizard
                    isBigNavigationMarginTop
                    skipButtonTextDefault={<Translation id="Skip" />}
                    nextButtonTextDefault={<Translation id="Next Step" />}
                    previousButtonTextDefault={<Translation id="Previous Step" />}
                    isLoading={newTerminationMutation.isLoading}
                    onIsWizardModeSet={setIsWizardMode}
                    onComplete={() => formRef.current?.submitForm()}
                  >
                    <WizardStep
                      isValid={isTerminationDetailsValid}
                      name={<Translation id="Details" />}
                    >
                      <NewTerminationDetails />
                    </WizardStep>

                    <WizardStep isValid={isTypeValid} name={<Translation id="Type" />}>
                      <NewTerminationType costObjectId={values.costObjectId} />
                    </WizardStep>

                    <WizardStep
                      nextButtonText={<Translation id="Complete" />}
                      isValid={true}
                      name={<Translation id="Confirm" />}
                    >
                      <NewTerminationConfirm />
                    </WizardStep>
                  </Wizard>
                </Form>
              )
            }}
          </Formik>
        </>
      )}
    </Container>
  )
}
